import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, ArrowLeft, Clock, Star, Lightbulb, MessageCircle,
  CheckCircle, XCircle, Volume2, BookMarked, Sparkles, Send, X, Brain,
  CheckCircle2, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import FocusMode from '../components/focus/FocusMode';
import { Maximize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ReadingPage = () => {
  const { passageId } = useParams();
  const navigate = useNavigate();
  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWord, setSelectedWord] = useState(null);
  const [wordDefinition, setWordDefinition] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [pausePrompt, setPausePrompt] = useState(null);
  const [isMarkedRead, setIsMarkedRead] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [readingMode, setReadingMode] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    fetchPassage();
  }, [passageId]);

  const fetchPassage = async () => {
    try {
      setLoading(true);
      
      // Check if this is an AI-generated passage
      if (passageId === 'ai-generated') {
        const storedPassage = sessionStorage.getItem('ai_generated_passage');
        if (storedPassage) {
          const parsed = JSON.parse(storedPassage);
          setPassage(parsed);
          setLoading(false);
          return;
        }
      }
      
      // Fetch from API for regular passages
      const response = await axios.get(`${API}/api/elibrary/passages/${passageId}`);
      const data = response.data;
      
      // Normalize: 11+ passages use 'content' field, ensure 'excerpt' exists
      if (data.content && !data.excerpt) {
        data.excerpt = data.content;
      }
      
      setPassage(data);

      // Check if this passage is already read (from reading history)
      const token = localStorage.getItem('token');
      if (token && data.id) {
        try {
          const histRes = await axios.get(`${API}/api/elibrary/passages/reading-history/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const alreadyRead = (histRes.data.readings || []).some(r => r.passage_id === data.id && r.completed);
          setIsMarkedRead(alreadyRead);
        } catch (_) { /* silent */ }
      }
    } catch (error) {
      console.error('Error fetching passage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWordClick = async (word) => {
    const cleanWord = word.replace(/[.,!?;:"'()]/g, '').toLowerCase();
    if (cleanWord.length < 3) return;
    
    setSelectedWord(cleanWord);
    setWordDefinition(null);
    
    // For 11+ passages, use pre-built vocabulary data if available
    const vocabData = passage?.vocabulary_highlight_data;
    if (vocabData && typeof vocabData === 'object') {
      const match = Object.keys(vocabData).find(k => k.toLowerCase() === cleanWord);
      if (match) {
        const entry = vocabData[match];
        setWordDefinition({
          definition: entry.definition,
          example: entry.example,
          synonyms: entry.synonyms || [],
          antonym: null
        });
        return;
      }
    }
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/vocabulary-help?word=${encodeURIComponent(cleanWord)}&level=11plus`);
      setWordDefinition(response.data.help);
    } catch (error) {
      setWordDefinition({ 
        definition: "Definition not available. Try asking the Ed's Tutor!",
        example: "",
        synonyms: [],
        antonym: ""
      });
    }
  };

  const handleMarkRead = async () => {
    const token = localStorage.getItem('token');
    if (!token || !passage?.id) return;
    setMarkingRead(true);
    try {
      await axios.post(`${API}/api/elibrary/passages/mark-read`, {
        passage_id: passage.id,
        completed: true,
        time_spent: 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      setIsMarkedRead(true);
    } catch (error) {
      console.error('Mark as read failed:', error);
    } finally {
      setMarkingRead(false);
    }
  };
  const handleAIChat = async () => {
    if (!aiInput.trim()) return;
    
    const userMessage = aiInput;
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/tutor`, {
        text: userMessage,
        action: 'explain',
        context: passage?.excerpt?.substring(0, 500),
        level: '11plus'
      });
      
      setAiMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process that request. Please try again." 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleQuickAIAction = async (action, text) => {
    setAiChatOpen(true);
    setAiMessages(prev => [...prev, { role: 'user', content: `${action}: "${text?.substring(0, 100)}..."` }]);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/tutor`, {
        text: text,
        action: action.toLowerCase(),
        level: '11plus'
      });
      
      setAiMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process that request." 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Handle reading level adjustment
  const handleAdjustLevel = async (targetLevel) => {
    setAiChatOpen(true);
    const levelLabels = {
      'easier': 'an easier version',
      'harder': 'a more challenging version',
      'age7': 'a version for 7 year olds',
      'age10': 'a version for 10 year olds',
      'age14': 'a version for 14 year olds (GCSE level)',
      'age16': 'a version for 16 year olds (A-Level)'
    };
    setAiMessages(prev => [...prev, { 
      role: 'user', 
      content: `Please rewrite this passage as ${levelLabels[targetLevel] || 'a different reading level'}` 
    }]);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/tutor`, {
        text: passage.excerpt,
        action: 'adjust_level',
        target_level: targetLevel,
        level: '11plus'
      });
      
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `📚 **Rewritten for ${levelLabels[targetLevel]}:**\n\n${response.data.response}` 
      }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't adjust the reading level right now." 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Handle vocabulary scaffolding
  const handleVocabularyScaffold = async () => {
    const vocabWords = passage?.vocabulary_highlights || [];
    if (vocabWords.length === 0 && !selectedWord) {
      setAiChatOpen(true);
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "💡 Click on any word in the passage first, then use this feature to get deep vocabulary scaffolding - including why the author chose it, related words, and memory tricks!" 
      }]);
      return;
    }
    
    const wordToScaffold = selectedWord || vocabWords[0];
    setAiChatOpen(true);
    setAiMessages(prev => [...prev, { 
      role: 'user', 
      content: `Help me deeply understand the word "${wordToScaffold}" from this passage` 
    }]);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/tutor`, {
        text: wordToScaffold,
        action: 'vocabulary_scaffold',
        context: passage.excerpt.substring(0, 500),
        level: '11plus'
      });
      
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't provide vocabulary scaffolding right now." 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Handle inference prompts
  const handleInferencePrompt = async () => {
    setAiChatOpen(true);
    setAiMessages(prev => [...prev, { 
      role: 'user', 
      content: `Help me think deeper about what the author might be suggesting in this passage` 
    }]);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/elibrary/ai/tutor`, {
        text: passage.excerpt,
        action: 'inference',
        level: '11plus'
      });
      
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `🤔 **Let's Think Deeper:**\n\n${response.data.response}` 
      }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't generate inference prompts right now." 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const renderInteractiveText = (text) => {
    if (!text) return null;
    
    const words = text.split(/(\s+)/);
    const highlightWords = passage?.vocabulary_highlights || [];
    
    return words.map((word, index) => {
      const cleanWord = word.replace(/[.,!?;:"'()]/g, '').toLowerCase();
      const isHighlighted = highlightWords.some(hw => hw.toLowerCase() === cleanWord);
      
      if (word.trim() === '') return <span key={index}>{word}</span>;
      
      return (
        <span
          key={index}
          onClick={() => handleWordClick(word)}
          className={`cursor-pointer transition-all hover:bg-violet-500/20 rounded px-0.5 ${
            isHighlighted ? 'text-violet-600 font-medium underline decoration-dotted underline-offset-4' : ''
          } ${selectedWord === cleanWord ? 'bg-violet-500/30 text-violet-600' : ''}`}
        >
          {word}
        </span>
      );
    });
  };

  const calculateScore = () => {
    if (!passage?.comprehension_questions) return 0;
    let correct = 0;
    passage.comprehension_questions.forEach((q, idx) => {
      if (parseInt(answers[idx]) === q.correct) correct++;
    });
    return Math.round((correct / passage.comprehension_questions.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-violet-600 animate-pulse mx-auto mb-4" />
          <p className="text-slate-500">Loading passage...</p>
        </div>
      </div>
    );
  }

  if (!passage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Passage not found</p>
          <Button onClick={() => navigate(-1)} className="bg-violet-600" data-testid="back-btn">
            Back to Library
          </Button>
        </div>
      </div>
    );
  }

  // Seneca-style Reading Mode — minimal chrome, large type, full-width passage.
  if (readingMode && passage) {
    return (
      <FocusMode
        title={`Reading · ${passage.author || ''}`}
        current={1}
        total={1}
        progressLabel={null}
        onExit={() => setReadingMode(false)}
        showEstimate={false}
      >
        <h1 className="text-3xl md:text-4xl font-bold mb-2 leading-tight" data-testid="reading-mode-title">
          {passage.title}
        </h1>
        {passage.author && (
          <p className="opacity-70 mb-6 text-base">by {passage.author}</p>
        )}
        <div
          className="whitespace-pre-wrap leading-[1.9] text-slate-800 dark:text-slate-100"
          style={{ fontFamily: "'Georgia', serif" }}
          data-testid="reading-mode-content"
        >
          {passage.excerpt}
        </div>
      </FocusMode>
    );
  }

  // Seneca-style Quiz Mode — wrap comprehension questions in FocusMode so
  // students answer one question per screen with proper progress + a11y.
  if (showQuiz && passage?.comprehension_questions?.length > 0 && !showResults) {
    const qList = passage.comprehension_questions;
    const idx = Math.min(currentQuestion, qList.length - 1);
    const q = qList[idx];
    const allAnswered = Object.keys(answers).length >= qList.length;
    return (
      <FocusMode
        title={`Comprehension · ${passage.title}`}
        current={idx + 1}
        total={qList.length}
        onPrev={() => setCurrentQuestion((p) => Math.max(p - 1, 0))}
        onNext={() => {
          if (idx < qList.length - 1) setCurrentQuestion((p) => p + 1);
          else if (allAnswered) setShowResults(true);
        }}
        canPrev={idx > 0}
        canNext={idx < qList.length - 1 || allAnswered}
        onExit={() => setShowQuiz(false)}
        showEstimate
      >
        <div className="flex items-center gap-2 mb-4 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold">
            Comprehension Check
          </span>
        </div>
        <h2 className="leading-snug font-semibold mb-6 text-slate-900 dark:text-slate-100" data-testid="focus-quiz-question">
          {q.question}
        </h2>
        <RadioGroup
          value={answers[idx]?.toString() ?? ''}
          onValueChange={(value) => setAnswers({ ...answers, [idx]: parseInt(value, 10) })}
          className="space-y-3"
        >
          {q.options.map((option, oIdx) => {
            const isSelected = answers[idx] === oIdx;
            return (
              <label
                key={oIdx}
                htmlFor={`quiz-opt-${oIdx}`}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer text-slate-900 dark:text-slate-100 focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 focus-within:ring-offset-transparent ${
                  isSelected
                    ? 'bg-purple-500/10 border-purple-500'
                    : 'border-current/20 hover:border-purple-500 hover:bg-purple-500/5'
                }`}
                data-testid={`focus-quiz-option-${oIdx}`}
              >
                <RadioGroupItem value={oIdx.toString()} id={`quiz-opt-${oIdx}`} />
                <span className="flex-1">{option}</span>
              </label>
            );
          })}
        </RadioGroup>
        {allAnswered && (
          <div className="mt-6 pt-4 border-t border-current/10 flex items-center justify-end">
            <Button
              onClick={() => setShowResults(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="focus-quiz-finish-btn"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> Finish Quiz
            </Button>
          </div>
        )}
      </FocusMode>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white">
      {/* Mobile vocabulary bottom sheet */}
      <AnimatePresence>
        {selectedWord && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
          >
            <div className="bg-white border-t border-violet-500/30 rounded-t-2xl p-4 pb-safe max-h-[55vh] overflow-y-auto shadow-2xl">
              {/* drag handle */}
              <div className="w-10 h-1 rounded-full bg-slate-100 mx-auto mb-4" />
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-slate-900 font-bold text-lg capitalize">{selectedWord}</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedWord(null)} className="text-slate-500 h-8 w-8 p-0 -mt-1">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {wordDefinition ? (
                <div className="space-y-3">
                  <p className="text-slate-700 text-sm leading-relaxed">{wordDefinition.definition}</p>
                  {wordDefinition.example && (
                    <p className="text-slate-500 italic text-sm">"{wordDefinition.example}"</p>
                  )}
                  {wordDefinition.synonyms?.length > 0 && (
                    <p className="text-sm">
                      <span className="text-slate-500">Synonyms: </span>
                      <span className="text-emerald-600">{wordDefinition.synonyms.join(', ')}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Looking up definition...</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(-1)}
              className="text-slate-500 hover:text-slate-900 px-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Back</span>
            </Button>
            
            <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
              <Badge className="bg-violet-600/20 text-violet-600 border border-violet-500/30 hidden sm:inline-flex">
                {passage.difficulty}
              </Badge>
              {/* Mark as Read / Re-read button - for 11+ themed passages */}
              {passage.vocabulary_highlight_data && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkRead}
                  disabled={markingRead}
                  data-testid="mark-read-btn"
                  className={`h-8 px-2 sm:px-3 text-xs sm:text-sm ${isMarkedRead 
                    ? 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10'
                    : 'border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10'}`}
                >
                  {markingRead ? (
                    <RefreshCw className="w-3.5 h-3.5 sm:mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 sm:mr-1" />
                  )}
                  <span className="hidden sm:inline">{isMarkedRead ? 'Re-read' : 'Mark as Read'}</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReadingMode(true)}
                className="h-8 px-2 sm:px-3 border-sky-500/30 text-sky-600 hover:bg-sky-500/10 text-xs sm:text-sm"
                data-testid="reading-mode-btn"
                aria-label="Open Reading Mode"
              >
                <Maximize2 className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Reading Mode</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAiChatOpen(!aiChatOpen)}
                className="h-8 px-2 sm:px-3 border-amber-500/30 text-amber-600 hover:bg-amber-500/10 text-xs sm:text-sm"
              >
                <Sparkles className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Ed's Tutor</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Reading Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Card */}
            <Card className="bg-slate-100 border-slate-200">
              <CardContent className="p-6">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                  {passage.title}
                </h1>
                <p className="text-slate-500 mb-4">by {passage.author}</p>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {passage.reading_time}
                  </span>
                  <span>{passage.word_count} words</span>
                  {passage.themes?.map((theme) => (
                    <Badge key={theme} variant="outline" className="border-slate-200 text-slate-500">
                      {theme}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Reading Content */}
            <Card className="bg-slate-100 border-slate-200">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <div className="prose prose-invert max-w-none">
                  <div 
                    ref={contentRef}
                    className="text-slate-700 text-base sm:text-lg leading-relaxed sm:leading-[1.9] whitespace-pre-wrap"
                    style={{ fontFamily: "'Georgia', serif" }}
                  >
                    {renderInteractiveText(passage.excerpt)}
                  </div>
                </div>
                
                {/* Vocabulary Highlights Legend */}
                {passage.vocabulary_highlights?.length > 0 && (
                  <div className="mt-4 pt-4 sm:mt-6 sm:pt-6 border-t border-slate-200">
                    <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span><span className="text-violet-600 underline decoration-dotted">Highlighted words</span> are vocabulary to learn. Tap any word for its definition!</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comprehension Quiz */}
            {passage.comprehension_questions?.length > 0 && (
              <Card className="bg-slate-100 border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <BookMarked className="w-5 h-5 text-emerald-600" />
                    Comprehension Check
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!showQuiz ? (
                    <Button 
                      onClick={() => setShowQuiz(true)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      Start Quiz ({passage.comprehension_questions.length} questions)
                    </Button>
                  ) : showResults ? (
                    <div className="text-center py-6">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-violet-500/20 flex items-center justify-center mx-auto mb-4 border border-slate-200">
                        <span className="text-3xl font-bold text-slate-900">{calculateScore()}%</span>
                      </div>
                      <p className="text-slate-500 mb-4">
                        You got {Object.keys(answers).filter(idx => parseInt(answers[idx]) === passage.comprehension_questions[idx].correct).length} out of {passage.comprehension_questions.length} correct!
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setShowResults(false);
                            setCurrentQuestion(0);
                            setAnswers({});
                          }}
                          className="border-slate-200"
                        >
                          Try Again
                        </Button>
                        <Button 
                          onClick={() => navigate(-1)}
                          className="bg-violet-600"
                        >
                          Back
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Progress 
                        value={((currentQuestion + 1) / passage.comprehension_questions.length) * 100} 
                        className="mb-6 h-2"
                      />
                      
                      <div className="mb-6">
                        <p className="text-sm text-slate-500 mb-2">
                          Question {currentQuestion + 1} of {passage.comprehension_questions.length}
                        </p>
                        <p className="text-slate-900 text-lg font-medium">
                          {passage.comprehension_questions[currentQuestion].question}
                        </p>
                      </div>
                      
                      <RadioGroup
                        value={answers[currentQuestion]?.toString()}
                        onValueChange={(value) => setAnswers({...answers, [currentQuestion]: parseInt(value)})}
                        className="space-y-3"
                      >
                        {passage.comprehension_questions[currentQuestion].options.map((option, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center space-x-3 p-3 rounded-lg bg-slate-100 hover:bg-slate-100 transition-colors"
                          >
                            <RadioGroupItem 
                              value={idx.toString()} 
                              id={`option-${idx}`}
                              className="border-slate-200"
                            />
                            <Label 
                              htmlFor={`option-${idx}`}
                              className="text-slate-700 cursor-pointer flex-1"
                            >
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                      
                      <div className="flex justify-between mt-5 sm:mt-6 gap-3">
                        <Button
                          variant="outline"
                          disabled={currentQuestion === 0}
                          onClick={() => setCurrentQuestion(prev => prev - 1)}
                          className="border-slate-200 flex-1 sm:flex-none"
                        >
                          Previous
                        </Button>
                        
                        {currentQuestion < passage.comprehension_questions.length - 1 ? (
                          <Button
                            onClick={() => setCurrentQuestion(prev => prev + 1)}
                            disabled={answers[currentQuestion] === undefined}
                            className="bg-violet-600 flex-1 sm:flex-none"
                          >
                            Next
                          </Button>
                        ) : (
                          <Button
                            onClick={() => setShowResults(true)}
                            disabled={Object.keys(answers).length < passage.comprehension_questions.length}
                            className="bg-emerald-600 flex-1 sm:flex-none"
                          >
                            Submit
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Word Definition Card */}
            <AnimatePresence>
              {selectedWord && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className="bg-gradient-to-br from-violet-600/20 to-indigo-600/20 border-violet-500/30">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-slate-900 capitalize">{selectedWord}</CardTitle>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedWord(null)}
                          className="text-slate-500 h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {wordDefinition ? (
                        <div className="space-y-3">
                          <p className="text-slate-700">{wordDefinition.definition}</p>
                          {wordDefinition.example && (
                            <p className="text-slate-500 italic text-sm">"{wordDefinition.example}"</p>
                          )}
                          {wordDefinition.memory_trick && (
                            <div className="flex items-start gap-2 mt-2 p-2 rounded bg-amber-500/10">
                              <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <p className="text-amber-600 text-sm">{wordDefinition.memory_trick}</p>
                            </div>
                          )}
                          {wordDefinition.synonyms?.length > 0 && (
                            <div className="text-sm">
                              <span className="text-slate-500">Synonyms: </span>
                              <span className="text-emerald-600">{wordDefinition.synonyms.join(', ')}</span>
                            </div>
                          )}
                          {wordDefinition.antonym && (
                            <div className="text-sm">
                              <span className="text-slate-500">Antonym: </span>
                              <span className="text-rose-600">{wordDefinition.antonym}</span>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleQuickAIAction('example', selectedWord)}
                              className="border-slate-200 text-xs"
                            >
                              More Examples
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-slate-500">
                          <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                          Loading definition...
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* AI Learning Tools - Enhanced */}
            <Card className="bg-slate-100 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  AI Learning Tools
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Understanding Section */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Understanding</p>
                  <div className="space-y-2">
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => handleQuickAIAction('simplify', passage.excerpt)}
                      data-testid="ai-simplify-btn"
                    >
                      <Lightbulb className="w-4 h-4 mr-2 text-emerald-600" />
                      Explain Simply
                    </Button>
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => handleQuickAIAction('summarize', passage.excerpt)}
                      data-testid="ai-summarize-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2 text-violet-600" />
                      Quick Summary
                    </Button>
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => handleQuickAIAction('explain', passage.excerpt)}
                      data-testid="ai-explain-btn"
                    >
                      <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                      Detailed Explanation
                    </Button>
                  </div>
                </div>

                {/* Reading Level Section */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Adjust Reading Level</p>
                  <div className="grid grid-cols-2 gap-2 w-full">
                    <Button 
                      size="sm"
                      className="justify-start bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-600 border border-cyan-500/30 text-xs"
                      onClick={() => handleAdjustLevel('easier')}
                      data-testid="level-easier-btn"
                    >
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Easier
                    </Button>
                    <Button 
                      size="sm"
                      className="justify-start bg-orange-600/20 hover:bg-orange-600/30 text-orange-600 border border-orange-500/30 text-xs"
                      onClick={() => handleAdjustLevel('harder')}
                      data-testid="level-harder-btn"
                    >
                      Harder
                      <ArrowLeft className="w-3 h-3 ml-1 rotate-180" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[
                      { level: 'age7', label: 'Age 7', color: 'blue' },
                      { level: 'age10', label: 'Age 10', color: 'green' },
                      { level: 'age14', label: 'Age 14', color: 'purple' },
                      { level: 'age16', label: 'Age 16', color: 'rose' }
                    ].map(({ level, label, color }) => (
                      <Button 
                        key={level}
                        size="sm"
                        variant="ghost"
                        className={`text-xs px-2 py-1 h-auto text-${color}-400 hover:bg-${color}-500/10`}
                        onClick={() => handleAdjustLevel(level)}
                        data-testid={`level-${level}-btn`}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Vocabulary Section */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Vocabulary Help</p>
                  <div className="space-y-2">
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => handleVocabularyScaffold()}
                      data-testid="vocab-scaffold-btn"
                    >
                      <Star className="w-4 h-4 mr-2 text-amber-600" />
                      Vocabulary Scaffolding
                    </Button>
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => handleQuickAIAction('contextual_example', selectedWord || 'this concept')}
                      data-testid="contextual-example-btn"
                    >
                      <Lightbulb className="w-4 h-4 mr-2 text-teal-600" />
                      Contextual Examples
                    </Button>
                  </div>
                </div>

                {/* Deep Thinking Section */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Deep Thinking</p>
                  <div className="space-y-2">
                    <Button 
                      className="w-full justify-start bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30 text-white text-sm border border-violet-500/20"
                      onClick={() => handleInferencePrompt()}
                      data-testid="inference-btn"
                    >
                      <Brain className="w-4 h-4 mr-2 text-violet-600" />
                      Inference Prompts
                    </Button>
                    <Button 
                      className="w-full justify-start bg-slate-100 hover:bg-slate-100 text-slate-900 text-sm"
                      onClick={() => setAiChatOpen(true)}
                      data-testid="ask-question-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2 text-amber-600" />
                      Ask Any Question
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Literary Devices (if poetry) */}
            {passage.literary_devices?.length > 0 && (
              <Card className="bg-slate-100 border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-900 text-lg">Literary Devices</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {passage.literary_devices.map((device, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-slate-100">
                      <p className="text-violet-600 font-medium text-sm">{device.device}</p>
                      <p className="text-slate-600 text-sm italic">"{device.example}"</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* AI Chat Drawer */}
      <AnimatePresence>
        {aiChatOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white border-l border-slate-200 z-50 flex flex-col"
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <h3 className="text-slate-900 font-semibold">Ed's Tutor</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setAiChatOpen(false)}>
                <X className="w-5 h-5 text-slate-500" />
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {aiMessages.length === 0 && (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-violet-600/30 mx-auto mb-4" />
                    <p className="text-slate-500 text-sm">Ask me anything about this passage!</p>
                    <p className="text-slate-500 text-xs mt-2">
                      Try: "Explain this paragraph" or "What does this word mean?"
                    </p>
                  </div>
                )}
                {aiMessages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[85%] p-3 rounded-2xl ${
                        msg.role === 'user' 
                          ? 'bg-violet-600 text-white rounded-br-sm' 
                          : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 text-slate-500 p-3 rounded-2xl rounded-bl-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            <div className="p-4 border-t border-slate-200">
              <div className="flex gap-2">
                <Input
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAIChat()}
                  placeholder="Ask a question..."
                  className="bg-slate-100 border-slate-200 text-slate-900"
                />
                <Button onClick={handleAIChat} disabled={aiLoading} className="bg-violet-600">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReadingPage;
